import { useEffect, useRef } from "react";
import styled from "styled-components";
import { tokens } from "../../styles";

/*
  Descent: a dotted loss landscape that reshapes between terrains while a
  single ball stays on it. When the terrain changes, the ball is carried
  along on the surface at its old minimum, which has usually become a slope,
  then rolls downhill with momentum to the new minimum, leaving a dashed
  trail. Paths are simulated up front, so every run plays back smoothly and
  identically. The animation pauses while off screen and shows a still frame
  for visitors who prefer reduced motion.

  CHAIN is the order of terrains (indices into TERRAINS). It is chosen so that
  every hand-off makes the ball travel a real distance to a different minimum,
  and so the loop closes: the last minimum is where the first terrain starts.
  If you edit a terrain or reorder CHAIN, check that the ball still moves
  noticeably on each transition.
*/

const W = 520;
const H = 180;
const COLS = 84;
const ROWS = 29;
const MORPH = 2.0; // s, terrain reshaping (ball rides the surface)
const HOLD = 0.3;  // s, ball sits at its new height before it rolls
const REST = 0.6;  // s, ball at rest before the next terrain
const ROLL = 90;   // path steps per second while rolling
const BALL = "#2b2b2b";
const TRAIL = "rgba(70, 66, 60, 0.85)"; // dashed trail
const DASH = [4, 3];

const { exp: E, sin: S, cos: C, pow: pw, PI } = Math;

// Each terrain: a height function over u, v in [-1, 1] and a seed start point.
const TERRAINS = [
  // 0: Wide flat basin next to a sharp well
  { f: (u, v) => 0.25 * u * u + 0.15 * v * v + 0.06 * S(3 * v + 2 * u) - 0.45 * E(-pw(pw((u + 0.35) / 0.38, 2) + pw(v / 0.55, 2), 2)) - 0.5 * E(-(pw((u - 0.45) / 0.09, 2) + pw((v + 0.05) / 0.14, 2))), start: [-0.9, 0.8] },
  // 1: Curving ravine
  { f: (u, v) => Math.min(0.5, 1.4 * pw(v - 0.35 * S(2.2 * u), 2)) + 0.22 * pw(u + 0.55, 2), start: [-0.9, 0.65] },
  // 2: Saddle with a valley on each side (not used in CHAIN)
  { f: (u, v) => 0.32 * (u * u - 1.3 * v * v) + 0.9 * pw(v, 4) + 0.04 * S(4 * u + 1), start: [0.45, 0.8] },
  // 3: Rosenbrock's banana-shaped valley
  { f: (u, v) => { const x = u * 1.6, y = v * 1.6 + 0.6; return Math.log1p(0.2 * pw(1 - x, 2) + 1.2 * pw(y - x * x, 2)); }, start: [0, 0.65] },
  // 4: Ring-shaped trough
  { f: (u, v) => pw(Math.hypot(u, v * 1.35) - 0.55, 2) + 0.05 * u + 0.03 * S(3 * Math.atan2(v, u)), start: [0.9, 0.8] },
  // 5: Two wells of different depth (not used in CHAIN)
  { f: (u, v) => 2 * pw(u * u - 0.36, 2) + 0.35 * v * v + 0.1 * u, start: [0, -0.85] },
  // 6: Bowl covered in ripples (the ball gets caught in one)
  { f: (u, v) => 0.5 * (u * u + v * v) + 0.07 * (2 - C(3 * PI * u) - C(3 * PI * v)), start: [0.9, -0.4], relief: 0.05 },
  // 7: Rugged terrain with several dips
  { f: (u, v) => 0.12 * S(3.1 * u + 0.5) * C(2.7 * v) + 0.1 * S(5.3 * u - 1) * S(4.1 * v + 0.7) - 0.3 * E(-(pw(u - 0.3, 2) + pw(v + 0.2, 2)) / 0.08) - 0.22 * E(-(pw(u + 0.5, 2) + pw(v - 0.3, 2)) / 0.06) + 0.08 * u * u, start: [-0.9, 0.05], relief: 0.07 },
];

// Order the terrains play in. Every consecutive pair (and last -> first) was
// checked to send the ball at least ~0.4 units to a new minimum.
const CHAIN = [1, 3, 6, 4, 0, 7];

// Low-amplitude, multi-scale relief added to every terrain so none reads as flat.
// Each terrain gets its own phase so the texture differs between them.
function relief(u, v, k) {
  const a = 1.3 * k;
  return 0.5 * S(4.3 * u + 1.1 + a) * C(3.7 * v - 0.4 + a)
    + 0.32 * S(7.1 * u - 2 + 0.7 * a) * S(6.3 * v + 1.3 - a)
    + 0.18 * C(10.3 * u + 0.5 * a) * S(9.1 * v + 2.1);
}

// Height function for terrain k, normalized to [-0.42, 0.42] and bent so minima are steep wells.
function heightFn(tr, k, grid) {
  let lo = Infinity, hi = -Infinity;
  for (const p of grid) { const h = tr.f(p[0], p[1]); if (h < lo) lo = h; if (h > hi) hi = h; }
  const amt = (tr.relief ?? 0.14) * (hi - lo);
  const g = (u, v) => tr.f(u, v) + amt * relief(u, v, k);
  lo = Infinity; hi = -Infinity;
  for (const p of grid) { const h = g(p[0], p[1]); if (h < lo) lo = h; if (h > hi) hi = h; }
  return (u, v) => -0.42 + 0.84 * pw(Math.max(0, (g(u, v) - lo) / (hi - lo)), 0.65);
}

// Heavy-ball gradient descent from rest at `start`. Returns [u, v, height] steps.
function simulate(hn, start) {
  const path = [];
  let u = start[0], v = start[1], vu = 0, vv = 0, still = 0;
  const eps = 0.005;
  for (let s = 0; s < 480 && still < 30; s++) {
    const gu = (hn(u + eps, v) - hn(u - eps, v)) / (2 * eps);
    const gv = (hn(u, v + eps) - hn(u, v - eps)) / (2 * eps);
    vu = 0.9 * vu - 0.003 * gu;
    vv = 0.9 * vv - 0.003 * gv;
    u = Math.max(-1, Math.min(1, u + vu));
    v = Math.max(-1, Math.min(1, v + vv));
    path.push([u, v, hn(u, v)]);
    still = Math.hypot(vu, vv) < 0.0005 ? still + 1 : 0;
  }
  return path;
}

const ease = (u) => (u < 0.5 ? 4 * u * u * u : 1 - pw(-2 * u + 2, 3) / 2);

// Perspective projection; y is up. Returns [screenX, screenY, depthScale].
function project(x, y, z, yaw) {
  const pitch = 0.5, D = 3.4, scale = 76, cx = 260, cy = 119;
  const cyw = C(yaw), syw = S(yaw), cp = C(pitch), sp = S(pitch);
  const x1 = x * cyw - z * syw, z1 = x * syw + z * cyw;
  const y2 = y * cp - z1 * sp, z2 = y * sp + z1 * cp;
  const f = D / (D + z2);
  return [cx + x1 * f * scale, cy - y2 * f * scale, f];
}

function buildScene() {
  const grid = [];
  for (let j = 0; j < ROWS; j++) for (let i = 0; i < COLS; i++) grid.push([-1 + (2 * i) / (COLS - 1), -1 + (2 * j) / (ROWS - 1)]);

  const hns = CHAIN.map((k) => heightFn(TERRAINS[k], k, grid));
  const end = (path) => [path[path.length - 1][0], path[path.length - 1][1]];

  // Find where the ball sits when the loop restarts: the minimum the last
  // terrain leaves it in must be where the first terrain's run begins.
  let p = TERRAINS[CHAIN[0]].start;
  for (let it = 0; it < 8; it++) {
    let q = p;
    for (let i = 0; i < CHAIN.length; i++) q = end(simulate(hns[i], q));
    const closed = Math.hypot(q[0] - p[0], q[1] - p[1]) < 0.03;
    p = q;
    if (closed) break;
  }

  let t = 0;
  const segs = CHAIN.map((k, i) => {
    const hn = hns[i];
    const path = simulate(hn, p);
    const seg = {
      hn,
      heights: grid.map((g) => hn(g[0], g[1])),
      start: [p[0], p[1], hn(p[0], p[1])],
      path,
      t0: t,
    };
    p = end(path);
    seg.tMorph = t + MORPH;
    seg.tRoll = seg.tMorph + HOLD;
    seg.tRest = seg.tRoll + path.length / ROLL;
    seg.tEnd = seg.tRest + REST;
    t = seg.tEnd;
    return seg;
  });
  return { grid, segs, cycle: t };
}

function drawFrame(ctx, scene, time, ink) {
  const { grid, segs, cycle } = scene;
  const n = segs.length;
  const t = time % cycle;
  let k = 0;
  while (k < n - 1 && t >= segs[k].tEnd) k++;
  const cur = segs[k], prev = segs[(k + n - 1) % n];
  const e = t < cur.tMorph ? ease((t - cur.t0) / (cur.tMorph - cur.t0)) : 1;
  const yaw = 0.26 * S((2 * PI * time) / cycle);
  const P = (u, v, h) => project(u * 1.9, h * 1.1, v * 0.95, yaw);

  const dot = (x, y, r, a) => {
    if (a < 0.02 || r < 0.2) return;
    ctx.globalAlpha = Math.min(1, a);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, 6.2832);
    ctx.fill();
  };
  const ball = (q) => {
    ctx.fillStyle = BALL;
    dot(q[0], q[1], 3.1 * q[2], 1);
    ctx.fillStyle = ink;
  };
  // Dashed trail from the launch point to path step `upto`.
  const trail = (seg, upto, vis) => {
    if (upto < 2 || vis < 0.02) return;
    ctx.save();
    ctx.globalAlpha = vis;
    ctx.strokeStyle = TRAIL;
    ctx.lineWidth = 1.5;
    ctx.setLineDash(DASH);
    ctx.beginPath();
    let q = P(seg.start[0], seg.start[1], seg.start[2] + 0.012);
    ctx.moveTo(q[0], q[1]);
    for (let i = 0; i < upto; i++) {
      const p = seg.path[i];
      q = P(p[0], p[1], p[2] + 0.012);
      ctx.lineTo(q[0], q[1]);
    }
    ctx.stroke();
    ctx.restore();
  };

  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = ink;

  // Terrain
  for (let i = 0; i < grid.length; i++) {
    const h = prev.heights[i] + (cur.heights[i] - prev.heights[i]) * e;
    const q = P(grid[i][0], grid[i][1], h), low = 1 - (h + 0.42) / 0.84;
    dot(q[0], q[1], 1.2 * pw(q[2], 1.4), (0.3 + 0.62 * low) * (0.6 + 0.4 * q[2]));
  }

  // The previous trail fades out while the terrain reshapes
  if (e < 1) trail(prev, prev.path.length, 1 - e);

  // While the terrain reshapes (and for a beat after), the ball stays on the
  // surface at its old minimum and rides it up or down.
  if (t < cur.tRoll) {
    const s = cur.start;
    const h = prev.hn(s[0], s[1]) * (1 - e) + cur.hn(s[0], s[1]) * e;
    ball(P(s[0], s[1], h + 0.015));
    return;
  }

  // Ball rolls and comes to rest
  const idx = Math.min(cur.path.length, Math.floor((t - cur.tRoll) * ROLL));
  trail(cur, idx, 1);
  const b = idx > 0 ? cur.path[idx - 1] : cur.start;
  ball(P(b[0], b[1], b[2] + 0.015));
}

const Figure = styled.figure`
  margin: 0;
`;
const Canvas = styled.canvas`
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: ${W} / ${H};
`;

export default function Descent({ ink = tokens.blue, className }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return undefined;
    const scene = buildScene();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = Math.round(W * dpr);
    cv.height = Math.round(H * dpr);
    const ctx = cv.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      // A still frame: the first terrain with its finished path.
      drawFrame(ctx, scene, scene.segs[0].tRest + 0.5, ink);
      return undefined;
    }

    // Animation clock that only advances while the figure is on screen.
    let raf = 0, elapsed = 0, last = 0, visible = true;
    const loop = (now) => {
      if (last) elapsed += Math.min(0.1, (now - last) / 1000);
      last = now;
      drawFrame(ctx, scene, elapsed, ink);
      raf = visible ? requestAnimationFrame(loop) : 0;
      if (!raf) last = 0;
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !raf) raf = requestAnimationFrame(loop);
    });
    io.observe(cv);
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [ink]);

  return (
    <Figure className={className}>
      <Canvas
        ref={canvasRef}
        width={W}
        height={H}
        role="img"
        aria-label="A dotted loss landscape that changes shape while a ball rolls downhill into a new minimum each time"
      />
    </Figure>
  );
}